// A placed game table in 3D — any registered game (registry.ts).
//
// GameTableFurniture: the game's furniture (registry `Furniture`) plus an
// invisible seat marker on each chair, so the ordinary click-to-sit works and
// taking a seat in the game walks the avatar into that chair.
//
// ViewerGameTable: the live table in Play — follows the game state, draws the
// game's in-world view (registry `Table3D`), tracks whether you're standing
// at it (for the overlay prompt) and, in VR, mounts the game's hand-tappable
// panels (registry `VRPanel`).
//
// Frames: the primitive's origin is the centre of the invisible pick box, GT_H/2
// above the floor; everything a game draws is in the floor-origin group.

import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { Billboard, Text } from '@react-three/drei';
import { useXR } from '@react-three/xr';
import type { PrimitiveItem } from '../../stores/worldStore';
import { GT_H, SEAT_Y, TABLE_TOP } from './tableLayout';
import { useGames, followTable, mySeat, amHost, chairMarkers, claimNear } from './gameStore';
import { getGameDef, DEFAULT_GAME } from './registry';
import { noRaycast } from '../VR/vrUi';
import type { GameClientDef } from './types';

const _p = new THREE.Vector3();
const _c = new THREE.Vector3();

/** Furniture + chair seat markers, standing on the floor (drawn GT_H/2 below the pick-box centre). */
export function GameTableFurniture({ def, markersFor }: { def: GameClientDef; markersFor?: string }) {
  const markers = useRef<(THREE.Object3D | null)[]>([]);
  useEffect(() => {
    if (!markersFor) return;
    chairMarkers.set(markersFor, markers.current);
    return () => { if (chairMarkers.get(markersFor) === markers.current) chairMarkers.delete(markersFor); };
  }, [markersFor]);
  const Furniture = def.Furniture;
  return (
    <group position={[0, -GT_H / 2, 0]}>
      <Furniture />
      {/* Seat markers: what click-to-sit and the game aim for. tableTop is the
          play surface as an offset above the marker, so a seated player's eye
          can be kept above it (VR rig lift, first person). */}
      {def.chairs.map((ch, i) => {
        const markerY = (ch.y ?? SEAT_Y) - 0.05;
        const top = def.tableTop !== undefined ? def.tableTop : ch.y == null ? TABLE_TOP : null;
        return (
          <group key={i} position={[ch.x, 0, ch.z]} rotation={[0, ch.yaw, 0]}>
            <mesh
              ref={(m) => { markers.current[i] = m; }}
              position={[0, markerY, 0]}
              userData={top == null ? { seat: true, seatFit: 'chair' } : { seat: true, seatFit: 'chair', tableTop: top - markerY }}
            >
              <boxGeometry args={[0.46, 0.1, 0.46]} />
              <meshBasicMaterial transparent opacity={0} depthWrite={false} />
            </mesh>
          </group>
        );
      })}
    </group>
  );
}

/** Builder: furniture inside the invisible pick box. */
export function BuilderGameTable({ primitive }: { primitive: PrimitiveItem }) {
  const def = getGameDef(primitive.game);
  if (!def) return <UnknownGame kind={primitive.game} />;
  return (
    <>
      <GameTableFurniture def={def} />
      <Billboard position={[0, GT_H / 2 + 0.25, 0]}>
        <Text fontSize={0.16} color="#ffffff" outlineWidth={0.008} outlineColor="#000" raycast={noRaycast}>
          {`${def.label} table`}
        </Text>
      </Billboard>
    </>
  );
}

/** A table for a game this build doesn't know (newer world, older app): a label, nothing to play. */
function UnknownGame({ kind }: { kind?: string }) {
  return (
    <Billboard position={[0, 0, 0]}>
      <Text fontSize={0.12} color="#ffffff" outlineWidth={0.006} outlineColor="#000" raycast={noRaycast}>
        {`Game table (${kind || '?'}) — update the app to play`}
      </Text>
    </Billboard>
  );
}

export function ViewerGameTable({ id, primitive, worldId, avatarRef }: {
  id: string; primitive: PrimitiveItem; worldId: string; avatarRef?: React.MutableRefObject<THREE.Object3D | null>;
}) {
  const kind = primitive.game || DEFAULT_GAME;
  const def = getGameDef(kind);
  const groupRef = useRef<THREE.Group>(null);
  const { camera } = useThree();
  const presenting = useXR((s) => !!s.session);
  const entry = useGames((s) => s.tables[id]);
  const me = useGames((s) => s.me);
  const near = useGames((s) => s.near === id);
  const [following, setFollowing] = useState(false);
  const nearR = def?.nearRadius ?? 3.2;

  // Follow the table once a player comes within ~12 m (cheap for big worlds);
  // "near" (prompt / VR panel) within the game's radius on the same level.
  useFrame(() => {
    const g = groupRef.current;
    if (!g || !def) return;
    const src = presenting ? null : avatarRef?.current;
    if (src) src.getWorldPosition(_p); else camera.getWorldPosition(_p);
    g.getWorldPosition(_c);
    const d = Math.hypot(_p.x - _c.x, _p.z - _c.z);
    const sameLevel = Math.abs(_p.y - (_c.y - GT_H / 2)) < (src ? 1.5 : 2.6);
    if (!following && d < Math.max(12, nearR + 4)) setFollowing(true);
    const st = useGames.getState();
    const t = st.tables[id]?.table ?? null;
    const here = sameLevel && d < nearR;
    const joined = mySeat(t, st.me) >= 0 || amHost(t, st.me);
    // Compared over at most 4 m, so a 14 m trivia room doesn't outrank the table at your elbow.
    claimNear(id, here ? d / Math.min(nearR, 4) : joined ? 1000 + d : Infinity);
  });
  useEffect(() => {
    if (!following || !worldId || !def) return;
    return followTable(worldId, id, kind);
  }, [following, worldId, id, kind, def]);
  useEffect(() => () => claimNear(id, Infinity), [id]);

  const table = entry?.table ?? null;
  const seat = mySeat(table, me);
  const host = amHost(table, me);

  if (!def) {
    return <group position={primitive.position} rotation={primitive.rotation} scale={primitive.scale}><UnknownGame kind={kind} /></group>;
  }
  const { Table3D, VRPanel } = def;
  return (
    <group ref={groupRef} position={primitive.position} rotation={primitive.rotation} scale={primitive.scale}>
      <GameTableFurniture def={def} markersFor={id} />
      <group position={[0, -GT_H / 2, 0]}>
        {Table3D && <Table3D pid={id} worldId={worldId} def={def} primitive={primitive} entry={entry} table={table} me={me} seat={seat} isHost={host} />}
        {presenting && table && entry && (near || seat >= 0 || host) && (
          <VRPanel pid={id} worldId={worldId} def={def} entry={entry} table={table} me={me} seat={seat} isHost={host} />
        )}
      </group>
    </group>
  );
}
